import * as React from "react";
import { ColumnDef, Table } from "@tanstack/react-table";

import {
  DataTablePagination,
  DataTablePaginationProps,
} from "./data-table-pagination";
import DataTableColumns, { DataTableColumnsProps } from "./data-table-columns";
import DataTableBox, { RowDef } from "./data-table-box";
import { WithSetter, useTableAccordionGrouping } from "./data-table-context";
import { GalleryState } from "./data-table-gallery";
import { DataTableButtonsProps } from "./buttons/data-table-buttons";
import { useCssObserver } from "@/hooks/use-css-observer";
import { cn } from "@/lib/utils";

export type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  box?: RowDef<TData>;
  gallery?: WithSetter<GalleryState>;
  table: Table<TData>;
  isLoading?: boolean;
} & Omit<DataTableColumnsProps<TData, TValue>, "table" | "columns"> &
  DataTablePaginationProps<TData> & {
    pagination?: React.DetailedHTMLProps<
      React.HTMLAttributes<HTMLDivElement>,
      HTMLDivElement
    >;
    buttons?: DataTableButtonsProps;
    disableFilterBreadcrumbs?: boolean;
  };

export function DataTable<TData, TValue>({
  columns,
  table,
  total,
  count,
  itemType,
  pageOptions,
  loading,
  refresh,
  additional,
  top: top,
  box,
  scroll: _scroll,
  pagination,
  buttons,
  disableFilterBreadcrumbs,
  ShowPaginationOptions,
  className,
  ...props
}: DataTableProps<TData, TValue>) {
  const accordionGrouping = useTableAccordionGrouping();
  const paginationRef = React.useRef<HTMLDivElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  useCssObserver({
    target: paginationRef,
    storage: scrollRef,
    mapper: { "--table-pagination-height": "height" },
  });
  const scroll = React.useMemo(
    () =>
      _scroll
        ? {
            ..._scroll,
            ref: scrollRef,
            className: cn(
              "h-[calc(100%-var(--table-pagination-height)-0.5rem)]",
              _scroll.className,
            ),
          }
        : _scroll,
    [scrollRef, _scroll],
  );

  return (
    <div className={cn("@container/data-table h-full", className)}>
      <DataTablePagination
        {...pagination}
        ref={paginationRef}
        table={table}
        total={total}
        count={count}
        itemType={itemType}
        pageOptions={pageOptions}
        loading={loading}
        refresh={refresh}
        additional={additional}
        ShowPaginationOptions={ShowPaginationOptions}
        disableFilterBreadcrumbs={disableFilterBreadcrumbs}
        top={top}
      />

      {table.isGalleryEnabled() && table.getState().gallery ? (
        <DataTableBox
          {...props}
          table={table}
          columns={columns}
          box={box}
          loading={loading}
          scroll={scroll}
        />
      ) : (
        <DataTableColumns
          {...props}
          table={table}
          columns={columns}
          loading={loading}
          scroll={scroll}
          accordionGrouping={
            accordionGrouping.state.groupByColumn
              ? accordionGrouping.state
              : undefined
          }
          onToggleAccordionGroup={
            accordionGrouping.state.groupByColumn
              ? accordionGrouping.actions.toggleGroup
              : undefined
          }
        />
      )}
    </div>
  );
}
